import React from 'react';
import { FormatStrictCoundown } from '@components/basics/FormatStrictCountdown';
import { useAuth } from '@context/auth/base';
import { useIsSaleInMoreThanADay } from '../useIsSaleInMoreThanADay';
import { hasSaleStarted } from '../useMintState';
import type { ScheduleWithTierInfo } from './data';
import { hasSaleEnded, isScheduleSoldOut, useHasAccessToTieredSchedule } from './data';

const getGAMintComponentHeading = ({
  hasPublicSaleStarted,
  isSaleInMoreThanADay,
}: {
  isSaleInMoreThanADay: boolean;
  startTimestamp: number;
  hasPublicSaleStarted: boolean;
}) => {
  //public sale started
  if (hasPublicSaleStarted) {
    return null;
  }

  const publicSaleMessage = isSaleInMoreThanADay ? 'Starts on' : 'Starts in';

  if (!hasPublicSaleStarted) {
    return {
      message: publicSaleMessage,
      showTimer: true,
    };
  }

  return null;
};

export const useMintComponentHeading = ({
  isAuthenticatedWithAPI,
  schedule,
  contractAddress,
  chainId,
  hasPresale,
  hasPresaleAccess,
}: {
  isAuthenticatedWithAPI: boolean;
  schedule: ScheduleWithTierInfo;
  contractAddress: string;
  chainId: number;
  hasPresale: boolean;
  hasPresaleAccess: boolean;
}): { message: string; showTimer: boolean } | null => {
  const isSaleInMoreThanADay = useIsSaleInMoreThanADay({ timeStamp: schedule.startTime });
  const isSaleRunning = hasSaleStarted({
    startTimestamp: schedule.startTime,
  });

  const isSoldOut = isScheduleSoldOut({
    schedule,
  });

  const saleEnded = hasSaleEnded({
    schedule,
  });

  const { data: canMintSale = false } = useHasAccessToTieredSchedule({
    mintSchedule: schedule,
    editionAddress: contractAddress,
    chainId,
  });

  const isPresale = schedule.mode === 'VERIFY_MERKLE';

  const saleStartsMessage = isSaleInMoreThanADay ? 'Starts on' : 'Starts in';

  const publicSaleMessage = isSaleInMoreThanADay
    ? 'Public Sale starts on'
    : 'Public Sale starts in';
  const presaleMessage = isSaleInMoreThanADay ? 'Presale starts on' : 'Presale starts in';

  if (saleEnded || isSoldOut) return null;

  if (!isSaleRunning) {
    return {
      message: isPresale ? presaleMessage : hasPresale ? publicSaleMessage : saleStartsMessage,
      showTimer: true,
    };
  }

  // Sale has started
  if (isAuthenticatedWithAPI && hasPresaleAccess && !canMintSale) {
    return {
      message: "You've hit the cap!",
      showTimer: false,
    };
  }

  return null;
};

export const TieredMintComponentPanelSubInfo = ({
  schedule,
  contractAddress,
  chainId,
  hasPresale,
  hasPresaleAccess,
}: {
  schedule: ScheduleWithTierInfo;
  contractAddress: string;
  chainId: number;
  hasPresale: boolean;
  hasPresaleAccess: boolean;
}) => {
  const { isAuthenticatedWithAPI } = useAuth();

  const heading = useMintComponentHeading({
    isAuthenticatedWithAPI,
    schedule,
    contractAddress,
    chainId,
    hasPresale,
    hasPresaleAccess,
  });

  if (!heading) return null;

  return (
    <div className="flex flex-col items-center gap-0.5 pb-1 text-center font-base text-base-s text-white">
      <div className="inline-flex font-semibold">
        {heading.message}&nbsp;
        {heading.showTimer && (
          <FormatStrictCoundown timeStamp={schedule.startTime} expandedFormat hideUnit />
        )}
      </div>
    </div>
  );
};
export const MintComponentGAPanelSubInfo = ({ startTime }: { startTime: number }) => {
  const hasPublicSaleStarted = hasSaleStarted({
    startTimestamp: startTime,
  });

  const startTimestamp = startTime;

  const isSaleInMoreThanADay = useIsSaleInMoreThanADay({ timeStamp: startTimestamp });

  const heading = getGAMintComponentHeading({
    isSaleInMoreThanADay,
    hasPublicSaleStarted,
    startTimestamp,
  });

  if (!heading) return null;

  return (
    <div className="flex flex-col items-center gap-0.5 pb-1 text-center font-base text-base-s text-white">
      <div className="inline-flex font-semibold">
        {heading.message}&nbsp;
        {heading.showTimer && (
          <FormatStrictCoundown timeStamp={startTimestamp} expandedFormat hideUnit />
        )}
      </div>
    </div>
  );
};
